import { pageBySlug } from '../content/catalogue.js';
import { docBySlug } from '../content/docs.js';
import { ArrowRight } from '../ui/icons.js';
import { Layout } from './Layout.js';

/**
 * A trust document — plan §2, §13 and §17 (P2.3).
 *
 * Its own component rather than a reuse of `ToolPage`, for one reason that
 * matters: these pages must NOT carry a drop zone. Everything else on the site
 * is trying to get you to open a file; these are the pages you read before
 * deciding whether to, and putting a file picker on a threat model would be
 * both tonally wrong and a real hazard on the page that says "we would rather
 * not receive your documents".
 *
 * No JSON-LD either. `HowTo` and `FAQPage` do not describe a threat model, and
 * claiming a schema type a page does not fit is how a site earns a manual
 * action rather than a rich result.
 */

export interface DocPageProps {
  readonly slug: string;
}

export function DocPage({ slug }: DocPageProps) {
  const doc = docBySlug(slug);
  if (!doc) return null;

  return (
    <Layout title={doc.metaTitle} description={doc.metaDescription} canonical={`/${doc.slug}`}>
      <div className="relative isolate overflow-hidden border-b border-line">
        <div className="aurora" />
        <div className="shell-narrow relative z-10 pb-12 pt-12 sm:pt-16">
          <p className="eyebrow">{doc.group === 'trust' ? 'Trust' : 'Guide'}</p>
          <h1 className="display-sm optical-display mt-3">{doc.heading}</h1>
          <p className="prose-lead mt-5">{doc.intro}</p>
        </div>
      </div>

      <article className="shell-narrow py-12">
        {doc.sections.map((section) => (
          <section key={section.heading} className="mt-12 first:mt-0">
            <h2 className="headline">{section.heading}</h2>

            {section.paragraphs?.map((text) => (
              <p key={text} className="mt-3 leading-relaxed text-muted">
                {text}
              </p>
            ))}

            {section.callout && (
              <p
                className={`mt-4 rounded-xl border p-4 text-sm leading-relaxed ${
                  section.callout.tone === 'warn'
                    ? 'border-warn/40 bg-warn/[0.08] text-ink'
                    : 'border-line bg-surface-2 text-muted'
                }`}
              >
                {section.callout.text}
              </p>
            )}

            {section.table && (
              // Scrolls inside itself: a two-column table of prose is the one
              // thing on this site that genuinely cannot fit a narrow phone,
              // and the page body must never scroll sideways.
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      {section.table.columns.map((col, i) => (
                        <th
                          key={col || `col-${i}`}
                          scope="col"
                          className="py-2 pr-4 align-bottom text-[0.6875rem] font-620 uppercase tracking-wider text-faint"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.rows.map(([label, value]) => (
                      <tr key={label} className="border-b border-line last:border-0 align-top">
                        <th
                          scope="row"
                          className="w-2/5 min-w-[10rem] py-3 pr-4 font-550 leading-relaxed text-ink"
                        >
                          {label}
                        </th>
                        <td className="py-3 leading-relaxed text-muted">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {section.bullets && (
              <ul className="mt-4 space-y-2.5">
                {section.bullets.map((item) => (
                  <li key={item} className="flex gap-3 leading-relaxed text-muted">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-faint" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {doc.related && doc.related.length > 0 && (
          <nav className="mt-14">
            <p className="eyebrow">Tools here that can help</p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {doc.related.map((relatedSlug) => {
                const related = pageBySlug(relatedSlug);
                if (!related) return null;
                return (
                  <li key={relatedSlug}>
                    <a
                      href={`/${related.slug}`}
                      className="card card-link group flex items-center gap-3 p-4"
                    >
                      <span className="font-550 tracking-tight">{related.title}</span>
                      <ArrowRight className="ml-auto h-4 w-4 text-faint transition-colors group-hover:text-brand" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </article>
    </Layout>
  );
}
